import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Download, Link as LinkIcon, AlertCircle, CheckCircle, Loader, MapPin, FileText, ExternalLink, Image as ImageIcon, ChevronDown, ChevronUp } from 'lucide-react';
import { importGoogleMyMaps, ParsedPOI, ParsedCard } from '../utils/kmlParser';

const ImportContentPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [importUrl, setImportUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [parsedPOIs, setParsedPOIs] = useState<ParsedPOI[]>([]);
  const [parsedCards, setParsedCards] = useState<ParsedCard[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [expandedPOIs, setExpandedPOIs] = useState<Set<number>>(new Set());
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleImport = async () => {
    if (!importUrl.trim()) {
      setError('Please enter a Google MyMaps URL');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);
    setShowPreview(false);
    setExpandedPOIs(new Set());
    setExpandedCards(new Set());

    try {
      const data = await importGoogleMyMaps(importUrl);
      
      if (data.pois.length === 0) {
        setError('No points of interest found in the map');
        return;
      }

      setParsedPOIs(data.pois);
      setParsedCards(data.cards);
      setShowPreview(true);
      setSuccess(`Successfully fetched ${data.pois.length} POI(s) and ${data.cards.length} card(s)`);
    } catch (err: any) {
      console.error('Import error:', err);
      setError(err.message || 'Failed to import map data');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePOIExpanded = (index: number) => {
    setExpandedPOIs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const toggleCardExpanded = (index: number) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const togglePOISelection = (index: number) => {
    setParsedPOIs(prev => {
      const updated = [...prev];
      updated[index].selected = !updated[index].selected;
      return updated;
    });
  };

  const toggleCardSelection = (index: number) => {
    setParsedCards(prev => {
      const updated = [...prev];
      updated[index].selected = !updated[index].selected;
      return updated;
    });
  };

  const selectAllPOIs = () => {
    setParsedPOIs(prev => prev.map(poi => ({ ...poi, selected: true })));
  };

  const deselectAllPOIs = () => {
    setParsedPOIs(prev => prev.map(poi => ({ ...poi, selected: false })));
  };

  const selectAllCards = () => {
    setParsedCards(prev => prev.map(card => ({ ...card, selected: true })));
  };

  const deselectAllCards = () => {
    setParsedCards(prev => prev.map(card => ({ ...card, selected: false })));
  };

  const selectedPOIsCount = parsedPOIs.filter(poi => poi.selected).length;
  const selectedCardsCount = parsedCards.filter(card => card.selected).length;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Import Content from Google MyMaps
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Import points of interest from your Google MyMaps. Paste a map URL below to get started.
          </p>
        </div>

        {/* Import Form */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <div className="mb-4">
            <label htmlFor="importUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Google MyMaps URL
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <LinkIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  id="importUrl"
                  type="text"
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  placeholder="https://www.google.com/maps/d/u/0/viewer?mid=..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                  disabled={isLoading}
                />
              </div>
              <button
                onClick={handleImport}
                disabled={isLoading || !importUrl.trim()}
                className="flex items-center px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-md font-medium transition-colors"
              >
                {isLoading ? (
                  <>
                    <Loader className="h-5 w-5 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Download className="h-5 w-5 mr-2" />
                    Import
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Info box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              <strong>Note:</strong> The map must be set to "Public" or "Anyone with the link can view" for this import to work.
              The URL should look like: <code className="bg-blue-100 dark:bg-blue-900/40 px-1 rounded">https://www.google.com/maps/d/viewer?mid=...</code>
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mt-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4 flex items-start">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800 dark:text-green-300">{success}</p>
            </div>
          )}
        </div>

        {/* Preview Section */}
        {showPreview && (
          <div className="space-y-6">
            {/* POIs Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <MapPin className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-2" />
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      Points of Interest ({parsedPOIs.length})
                    </h2>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={selectAllPOIs}
                      className="text-sm px-3 py-1 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded"
                    >
                      Select All
                    </button>
                    <button
                      onClick={deselectAllPOIs}
                      className="text-sm px-3 py-1 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  {selectedPOIsCount} of {parsedPOIs.length} selected for import
                </p>
              </div>

              <div className="p-4 space-y-3">
                {parsedPOIs.map((poi, index) => {
                  const isExpanded = expandedPOIs.has(index);
                  const hasRichContent = poi.urls || poi.images || poi.htmlDescription;
                  
                  return (
                    <div
                      key={index}
                      className={`border rounded-lg ${
                        poi.selected
                          ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-60'
                      }`}
                    >
                      <div className="p-4">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={poi.selected}
                            onChange={() => togglePOISelection(index)}
                            className="h-5 w-5 mt-1 text-primary-600 focus:ring-primary-500 border-gray-300 rounded flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                  {poi.name}
                                </h3>
                                <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-600 dark:text-gray-400">
                                  <span className="flex items-center gap-1">
                                    <MapPin className="h-3.5 w-3.5" />
                                    {poi.latitude.toFixed(6)}, {poi.longitude.toFixed(6)}
                                  </span>
                                  {poi.category && (
                                    <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 rounded text-xs">
                                      {poi.category}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {hasRichContent && (
                                <button
                                  onClick={() => togglePOIExpanded(index)}
                                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 p-1"
                                >
                                  {isExpanded ? (
                                    <ChevronUp className="h-5 w-5" />
                                  ) : (
                                    <ChevronDown className="h-5 w-5" />
                                  )}
                                </button>
                              )}
                            </div>

                            {/* Basic description - always visible */}
                            {poi.description && !poi.htmlDescription && (
                              <p className={`mt-2 text-sm text-gray-700 dark:text-gray-300 ${!isExpanded ? 'line-clamp-2' : ''}`}>
                                {poi.description}
                              </p>
                            )}

                            {/* Expanded content */}
                            {isExpanded && (
                              <div className="mt-3 space-y-3">
                                {/* HTML Description */}
                                {poi.htmlDescription && (
                                  <div className="prose prose-sm dark:prose-invert max-w-none">
                                    <div 
                                      dangerouslySetInnerHTML={{ __html: poi.htmlDescription }}
                                      className="text-sm text-gray-700 dark:text-gray-300"
                                    />
                                  </div>
                                )}

                                {/* URLs */}
                                {poi.urls && poi.urls.length > 0 && (
                                  <div className="space-y-1.5">
                                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                      <LinkIcon className="h-3.5 w-3.5" />
                                      URLs ({poi.urls.length})
                                    </h4>
                                    <div className="space-y-1">
                                      {poi.urls.map((url, urlIndex) => (
                                        <a
                                          key={urlIndex}
                                          href={url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400 hover:underline break-all"
                                        >
                                          <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                                          {url}
                                        </a>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Images */}
                                {poi.images && poi.images.length > 0 && (
                                  <div className="space-y-2">
                                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                      <ImageIcon className="h-3.5 w-3.5" />
                                      Images ({poi.images.length})
                                    </h4>
                                    <div className="grid grid-cols-2 gap-2">
                                      {poi.images.map((imageUrl, imgIndex) => (
                                        <div key={imgIndex} className="relative group">
                                          <img
                                            src={imageUrl}
                                            alt={`${poi.name} - Image ${imgIndex + 1}`}
                                            className="w-full h-40 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                                            onError={(e) => {
                                              const target = e.target as HTMLImageElement;
                                              target.style.display = 'none';
                                            }}
                                          />
                                          <a
                                            href={imageUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100"
                                          >
                                            <ExternalLink className="h-6 w-6 text-white" />
                                          </a>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Show expand hint if not expanded */}
                            {!isExpanded && hasRichContent && (
                              <button
                                onClick={() => togglePOIExpanded(index)}
                                className="mt-2 text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                              >
                                {poi.urls && <span>• {poi.urls.length} URL{poi.urls.length > 1 ? 's' : ''}</span>}
                                {poi.images && <span>• {poi.images.length} Image{poi.images.length > 1 ? 's' : ''}</span>}
                                {poi.htmlDescription && <span>• HTML content</span>}
                                <ChevronDown className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Cards Section */}
            {parsedCards.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FileText className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-2" />
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        Content Cards ({parsedCards.length})
                      </h2>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={selectAllCards}
                        className="text-sm px-3 py-1 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded"
                      >
                        Select All
                      </button>
                      <button
                        onClick={deselectAllCards}
                        className="text-sm px-3 py-1 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    {selectedCardsCount} of {parsedCards.length} selected for import
                  </p>
                </div>

                <div className="p-4 space-y-3">
                  {parsedCards.map((card, index) => {
                    const isExpanded = expandedCards.has(index);
                    const hasRichContent = card.urls || card.images || card.htmlText;
                    
                    return (
                      <div
                        key={index}
                        className={`border rounded-lg ${
                          card.selected
                            ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                            : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-60'
                        }`}
                      >
                        <div className="p-4">
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={card.selected}
                              onChange={() => toggleCardSelection(index)}
                              className="h-5 w-5 mt-1 text-primary-600 focus:ring-primary-500 border-gray-300 rounded flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                      {card.poiIndex >= 0 && card.poiIndex < parsedPOIs.length
                                        ? parsedPOIs[card.poiIndex].name
                                        : 'Unknown POI'}
                                    </h3>
                                    <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 rounded text-xs">
                                      {card.category}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      {card.language_code}
                                    </span>
                                  </div>
                                </div>
                                {hasRichContent && (
                                  <button
                                    onClick={() => toggleCardExpanded(index)}
                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 p-1"
                                  >
                                    {isExpanded ? (
                                      <ChevronUp className="h-5 w-5" />
                                    ) : (
                                      <ChevronDown className="h-5 w-5" />
                                    )}
                                  </button>
                                )}
                              </div>

                              {/* Basic text - always visible */}
                              {card.text && !card.htmlText && (
                                <p className={`mt-2 text-sm text-gray-700 dark:text-gray-300 ${!isExpanded ? 'line-clamp-3' : ''}`}>
                                  {card.text}
                                </p>
                              )}

                              {/* Expanded content */}
                              {isExpanded && (
                                <div className="mt-3 space-y-3">
                                  {/* HTML Content */}
                                  {card.htmlText && (
                                    <div className="prose prose-sm dark:prose-invert max-w-none">
                                      <div 
                                        dangerouslySetInnerHTML={{ __html: card.htmlText }}
                                        className="text-sm text-gray-700 dark:text-gray-300"
                                      />
                                    </div>
                                  )}

                                  {/* URLs */}
                                  {card.urls && card.urls.length > 0 && (
                                    <div className="space-y-1.5">
                                      <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                        <LinkIcon className="h-3.5 w-3.5" />
                                        URLs ({card.urls.length})
                                      </h4>
                                      <div className="space-y-1">
                                        {card.urls.map((url, urlIndex) => (
                                          <a
                                            key={urlIndex}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400 hover:underline break-all"
                                          >
                                            <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                                            {url}
                                          </a>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Images */}
                                  {card.images && card.images.length > 0 && (
                                    <div className="space-y-2">
                                      <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                        <ImageIcon className="h-3.5 w-3.5" />
                                        Images ({card.images.length})
                                      </h4>
                                      <div className="grid grid-cols-2 gap-2">
                                        {card.images.map((imageUrl, imgIndex) => (
                                          <div key={imgIndex} className="relative group">
                                            <img
                                              src={imageUrl}
                                              alt={`Card Image ${imgIndex + 1}`}
                                              className="w-full h-40 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                                              onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.style.display = 'none';
                                              }}
                                            />
                                            <a
                                              href={imageUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100"
                                            >
                                              <ExternalLink className="h-6 w-6 text-white" />
                                            </a>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Show expand hint if not expanded */}
                              {!isExpanded && hasRichContent && (
                                <button
                                  onClick={() => toggleCardExpanded(index)}
                                  className="mt-2 text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                                >
                                  {card.urls && <span>• {card.urls.length} URL{card.urls.length > 1 ? 's' : ''}</span>}
                                  {card.images && <span>• {card.images.length} Image{card.images.length > 1 ? 's' : ''}</span>}
                                  {card.htmlText && <span>• HTML content</span>}
                                  <ChevronDown className="h-3.5 w-3.5" />
                                </button>
                              )}

                              {/* Show full text button if not expanded */}
                              {!isExpanded && card.text && !card.htmlText && card.text.length > 150 && (
                                <button
                                  onClick={() => toggleCardExpanded(index)}
                                  className="mt-2 text-xs text-primary-600 dark:text-primary-400 hover:underline"
                                >
                                  Show full text
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Ready to import <strong>{selectedPOIsCount} POI(s)</strong> and <strong>{selectedCardsCount} card(s)</strong>
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Note: This is a preview only. Actual import functionality will be implemented in a future update.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowPreview(false);
                      setParsedPOIs([]);
                      setParsedCards([]);
                      setImportUrl('');
                      setSuccess(null);
                      setExpandedPOIs(new Set());
                      setExpandedCards(new Set());
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    disabled
                    className="px-6 py-2 bg-gray-400 text-white rounded-md font-medium cursor-not-allowed"
                    title="Import functionality coming soon"
                  >
                    Import Selected (Coming Soon)
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportContentPage;
